import {Link} from '@time-webkit/all/atoms';
import {PencilIcon, TrashDeleteIcon} from '@time-webkit/all/icons';
import {PopoverOverlay} from '@time-webkit/all/molecules/popover';

import {type Editor} from '@tiptap/core';
import {TimeWebkitLinkExtension} from 'packages/editor/src/extensions/link/link';

import {sendToStatist} from '@time-webkit/statist';

import {useEffect} from 'react';

import {useIntl} from 'react-intl';

import {ActionButton} from '../../action-button';
import {ShortcutTooltip} from '../../shortcut-tooltip';
import {useLinkPreviewContext} from '../context/link-preview';
import {InlineLinkForm} from '../floating-link-popover-form/inline-link-form';
import {prepareForm, hasProtocol} from '../utils';

import styles from './link-preview.module.css';

type Props = {
    editor: Editor;
}

const getLinkValueFromSelection = (editor: Editor | undefined) => {
    const selection = editor?.state.selection;

    if (!selection || selection.empty) {
        return '';
    }

    const hrefs = new Set<string>();

    editor?.state.doc.nodesBetween(selection.from, selection.to, (node) => {
        const mark = node.marks.find((m) => m.type.name === TimeWebkitLinkExtension.name);

        if (mark) {
            hrefs.add(mark.attrs.href);
        }
    });

    return hrefs.size === 1 ? hrefs.values().next().value : '';
};

export const LinkPreview = ({editor}: Props) => {
    const {formatMessage} = useIntl();
    const {isOpened, isEditing, triggerNode, handleLinkPreviewEvent} = useLinkPreviewContext();
    const url = triggerNode instanceof HTMLAnchorElement ? triggerNode.href : getLinkValueFromSelection(editor);

    useEffect(() => {
        if (isOpened) {
            sendToStatist('editor.link.preview');
        }
    }, [isOpened]);

    const handleMouseOver = (() => {
        if (isOpened) {
            handleLinkPreviewEvent('mouseover', triggerNode);
        }
    });
    const handleMouseLeave = (() => {
        handleLinkPreviewEvent('mouseout', null, 300);
    });
    const handleOpenChanged = ((isOpen: boolean) => {
        if (!isOpen) {
            handleLinkPreviewEvent('overlayclosed');

            if (isEditing) {
                editor.commands.toggleLinkHighlight(false, editor.state.selection.empty ? triggerNode : undefined);
            }
        }
    });
    const handleEdit = () => {
        handleLinkPreviewEvent('edit');
        editor.commands.toggleLinkHighlight(true, triggerNode);
    };
    const handleSubmit = (linkValue?: string, withHotkey = false) => {
        let href = linkValue?.trim() ?? '';

        if (href) {
            href = prepareForm(href).url;
        }

        if (!editor.state.selection.empty) {
            editor.commands.toggleLinkHighlight(false);
            handleLinkPreviewEvent('close');
            editor.commands.addLinkToSelection({href});
            return;
        }

        if (!triggerNode) {
            return;
        }

        editor.commands.toggleLinkHighlight(false, triggerNode);
        handleLinkPreviewEvent('close');

        const wasCalledByHandleDeleted = typeof linkValue === 'undefined';
        let isLinkDeleted = false;
        if (linkValue) {
            editor.commands.updateLink(triggerNode, href);
        } else {
            editor.commands.deleteLink(triggerNode);
            isLinkDeleted = true;
        }

        if (!wasCalledByHandleDeleted) {
            sendToStatist('editor.link.edit', {
                protocol: hasProtocol(linkValue),
                hotkey: withHotkey,
                delete: isLinkDeleted,
            });
        }
    };
    const handleDelete = () => {
        handleSubmit();
        sendToStatist('editor.link.delete');
    };

    const handleLinkClick = () => {
        sendToStatist('editor.link.open');
    };

    const renderPreview = () => (
        <div
            className={styles.linkPreview}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            data-testid='link-preview'
        >
            <Link
                className={styles.linkButton}
                size='medium'
                href={url}
                target='_blank'
                referrerPolicy='no-referrer'
                onClick={handleLinkClick}
            >
                {url}
            </Link>
            <div className={styles.actions}>
                <ShortcutTooltip
                    descriptor={{
                        id: 'shortcuts.msgs.markdown.link.edit',
                        defaultMessage: 'Edit',
                    }}
                >
                    <ActionButton
                        aria-label={formatMessage({
                            id: 'shortcuts.msgs.markdown.link.edit',
                            defaultMessage: 'Edit',
                        })}
                        icon={<PencilIcon className={styles.editIcon}/>}
                        onClick={handleEdit}
                    />
                </ShortcutTooltip>
                <ShortcutTooltip
                    descriptor={{
                        id: 'shortcuts.msgs.markdown.link.delete',
                        defaultMessage: 'Delete',
                    }}
                >
                    <ActionButton
                        aria-label={formatMessage({
                            id: 'shortcuts.msgs.markdown.link.delete',
                            defaultMessage: 'Delete',
                        })}
                        icon={<TrashDeleteIcon className={styles.deleteIcon}/>}
                        onClick={handleDelete}
                    />
                </ShortcutTooltip>
            </div>
        </div>
    );

    return (
        <PopoverOverlay
            triggerNode={triggerNode}
            placement='bottom start'
            isOpen={isOpened}
            offset={4}
            containerPadding={8}
            onOpenChange={handleOpenChanged}
            animations={{
                whenOpenClassName: styles.scaleIn,
                whenCloseClassName: styles.scaleOut,
            }}
        >
            {isEditing ? (
                <InlineLinkForm
                    onSubmit={handleSubmit}
                    value={url}
                    autoselection={true}
                />
            ) : renderPreview()}
        </PopoverOverlay>
    );
};
